import * as React from 'react';

import ToastActions from 'actions/ToastActions';
import ConfirmDialog from 'components/ConfirmDialog';
import Icon from 'components/Icon';
import ModalMenuItem from 'components/ModalMenuItem';
import { Props as MenuItemProps } from 'components/MenuItem';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import RemoveGroupOwnerModal from 'groups/components/GroupProfile/RemoveGroupOwnerModal';
import { useIntl } from 'lib/intl';
import type { UserActionsMenuButtonUser } from 'user/components/UserActionsMenuButton';
import errorLogger from 'utils/errorLogger';
import './styles/RemoveMemberMenuItem.scss';

// todo: refactor `GroupActions` `removeMember` to take just user uuid so we don't have to pass whole user
// todo: refactor UserActionsMenuButton test so we don't need test-selectors
export type Props = {
  dataTestSelector?: string;
  disabled?: boolean;
  group?: Group | undefined | null;
  groupName: string;
  groupUuid: string;
  memberIsOwner?: boolean;
  onRemove?: Function;
  memberName: string;
  memberUuid: string;
  member?: UserActionsMenuButtonUser;
} & MenuItemProps;

const RemoveMemberMenuItem: React.FC<Props> = ({
  dataTestSelector,
  disabled,
  eventElementName,
  group,
  groupName,
  groupUuid,
  memberIsOwner,
  onRemove,
  memberName,
  memberUuid,
  member,
}) => {
  const { formatMessage } = useIntl();
  const removeText = memberIsOwner
    ? formatMessage({ id: 'admin.classProfile.removeOwnerModal.remove' })
    : formatMessage({ id: 'admin.classProfile.removeMemberModal.remove' });
  const removeMember = () => {
    if (group && member) {
      const message = formatMessage(
        { id: 'admin.classProfile.removeMemberModal.successMessage' },
        { groupName, memberName }
      );
      GroupActions.removeMember(group, member)
        .then(() => {
          if (onRemove) {
            onRemove();
          }
          ToastActions.showSuccessToast(message);
        })
        .catch((e) => {
          const errorMessage =
            e.message ||
            formatMessage(
              { id: 'admin.classProfile.removeMemberModal.errorMessage' },
              { groupName, memberName }
            );
          ToastActions.showErrorToast(errorMessage);
          errorLogger('RemoveMemberMenuItem removeMember failed');
        });
    }
  };

  const renderRemoveParticipantDialog = () => {
    const subtextIntro = formatMessage({
      id: 'admin.classProfile.removeMemberModal.warningSubtext',
    });
    const messageHistoryWarning = formatMessage({
      id: 'admin.classProfile.removeMemberModal.messageHistoryWarning',
    });

    const subtext = (
      <React.Fragment>
        <div>{subtextIntro}</div>{' '}
        <div>
          {messageHistoryWarning}{' '}
          <Link target="_blank" href={LinkConstants.CONVERSATION_MESSAGE_HISTORY}>
            {formatMessage({ id: 'common.links.learnMore' })}
          </Link>
        </div>
      </React.Fragment>
    );

    return (
      <ConfirmDialog
        eventScreenName="RemoveMemberMenuItem"
        actionButtonColor="apple"
        actionButtonText={formatMessage({
          id: 'admin.classProfile.removeMemberModal.confirmButton',
        })}
        cancelButtonColor="mascot"
        cancelButtonLevel="text"
        cancelButtonText={formatMessage({
          id: 'admin.classProfile.removeMemberModal.cancelButton',
        })}
        size="xsmall"
        onConfirm={removeMember}
        title={formatMessage({ id: 'admin.classProfile.removeMemberModal.warningText' })}
        subtext={subtext}
      />
    );
  };

  return (
    <ModalMenuItem
      data-test-selector={dataTestSelector}
      disabled={disabled}
      eventElementName={eventElementName}
      modal={
        memberIsOwner ? (
          <RemoveGroupOwnerModal
            groupName={groupName}
            groupUuid={groupUuid}
            ownerName={memberName}
            ownerUuid={memberUuid}
          />
        ) : (
          renderRemoveParticipantDialog()
        )
      }
    >
      <span className="RemoveMemberMenuItem-icon">
        <Icon name="x" size="small" />
        <span>{removeText}</span>
      </span>
    </ModalMenuItem>
  );
};

export default RemoveMemberMenuItem;
